import DashboardLayout from '@/components/DashboardLayout'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { PhoneInput } from '@/components/ui/phone-input'
import { CpfInput } from '@/components/ui/cpf-input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { useForm, Controller, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { router } from '@inertiajs/react'
import { useState } from 'react'
import axios from 'axios'
import { Loader2 } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from 'sonner'

interface School {
  id: number
  name: string
  fiscal_enabled?: boolean
}

interface UsersNewProps {
  school: School
  context?: 'students' | 'team'
}

const userSchema = z.object({
  name: z.string().min(2, 'Nome deve ter no mínimo 2 caracteres'),
  email: z.string().email('Email inválido'),
  phone: z
    .string()
    .min(6, 'Telefone é obrigatório')
    .regex(/^[+]?[0-9\s\-\(\)]+$/, 'Telefone inválido'),
  document: z.string().optional(),
  birth_date: z.string().optional(),
  foreign_national: z.boolean(),
  role: z.enum(['student', 'teacher', 'manager'], {
    message: 'Selecione uma função',
  }),
  // Endereço fiscal (opcional no cadastro; exigido apenas na emissão da nota).
  zip_code: z.string().optional(),
  street: z.string().optional(),
  street_number: z.string().optional(),
  complement: z.string().optional(),
  neighborhood: z.string().optional(),
  address_city: z.string().optional(),
  address_state: z.string().optional(),
})

type UserFormData = z.infer<typeof userSchema>

export default function UsersNew({
  school,
  context = 'students',
}: UsersNewProps) {
  const isStudentContext = context === 'students'
  const defaultRole = isStudentContext ? 'student' : 'teacher'

  const {
    register,
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<UserFormData>({
    resolver: zodResolver(userSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      document: '',
      birth_date: '',
      foreign_national: false,
      role: defaultRole,
      zip_code: '',
      street: '',
      street_number: '',
      complement: '',
      neighborhood: '',
      address_city: '',
      address_state: '',
    },
  })

  const isForeign = useWatch({ control, name: 'foreign_national' })
  const [isLoadingCep, setIsLoadingCep] = useState(false)

  const handleCepSearch = async () => {
    const cleanCep = (getValues('zip_code') || '').replace(/\D/g, '')
    if (cleanCep.length !== 8) {
      toast.error('CEP deve ter 8 dígitos')
      return
    }

    setIsLoadingCep(true)
    try {
      const response = await axios.get(
        `https://viacep.com.br/ws/${cleanCep}/json/`,
      )

      if (response.data.erro) {
        toast.error('CEP não encontrado')
        return
      }

      setValue('street', response.data.logradouro || '')
      setValue('neighborhood', response.data.bairro || '')
      setValue('address_city', response.data.localidade || '')
      setValue('address_state', response.data.uf || '')

      toast.success('Endereço preenchido pelo CEP')
    } catch {
      toast.error('Erro ao buscar CEP. Verifique se o CEP está correto.')
    } finally {
      setIsLoadingCep(false)
    }
  }

  const onSubmit = async (data: UserFormData) => {
    const payload = { ...data }
    if (payload.foreign_national) {
      payload.document = ''
      // Esconder os campos não basta: o react-hook-form guarda o que já foi
      // digitado depois que eles saem da tela, então quem preencheu o endereço e
      // só então marcou "Estrangeiro" enviaria um CEP brasileiro junto.
      payload.zip_code = ''
      payload.street = ''
      payload.street_number = ''
      payload.complement = ''
      payload.neighborhood = ''
      payload.address_city = ''
      payload.address_state = ''
    }
    router.post('/users', payload, {
      onSuccess: () => {
        toast.success('Usuário criado com sucesso!')
      },
      onError: (errors) => {
        const errorMessage = Object.values(errors).flat().join(', ')
        toast.error(errorMessage || 'Erro ao criar usuário')
      },
    })
  }

  return (
    <DashboardLayout schoolName={school?.name}>
      <div className="p-8 space-y-6">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">
            {isStudentContext ? 'Novo Aluno' : 'Novo Membro da Equipe'}
          </h2>
          <p className="text-muted-foreground">
            {isStudentContext
              ? 'Adicione um novo aluno à sua escola de surf'
              : 'Adicione um novo professor ou gerente à equipe'}
          </p>
        </div>

        <Card className="max-w-2xl">
          <CardHeader>
            <CardTitle>
              {isStudentContext
                ? 'Informações do Aluno'
                : 'Informações do Membro'}
            </CardTitle>
            <CardDescription>
              A senha inicial será o número de telefone (sem formatação). O
              usuário poderá alterá-la após o primeiro login.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome Completo *</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Digite o nome completo do aluno"
                  {...register('name')}
                />
                {errors.name && (
                  <p className="text-sm text-destructive">
                    {errors.name.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="usuario@exemplo.com"
                  {...register('email')}
                />
                {errors.email && (
                  <p className="text-sm text-destructive">
                    {errors.email.message}
                  </p>
                )}
              </div>

              {/* CPF + toggle Estrangeiro — antes do Telefone */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="document">CPF</Label>
                  {isStudentContext && (
                    <div className="flex items-center gap-2">
                      <label
                        htmlFor="foreign_national"
                        className="text-sm text-muted-foreground cursor-pointer"
                      >
                        Estrangeiro
                      </label>
                      <Controller
                        name="foreign_national"
                        control={control}
                        render={({ field }) => (
                          <Switch
                            id="foreign_national"
                            checked={!!field.value}
                            onCheckedChange={field.onChange}
                          />
                        )}
                      />
                    </div>
                  )}
                </div>
                {!isForeign && (
                  <Controller
                    name="document"
                    control={control}
                    render={({ field }) => (
                      <CpfInput {...field} id="document" />
                    )}
                  />
                )}
                {errors.document && (
                  <p className="text-sm text-destructive">
                    {errors.document.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Telefone *</Label>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => <PhoneInput {...field} id="phone" />}
                />
                {errors.phone && (
                  <p className="text-sm text-destructive">
                    {errors.phone.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="birth_date">Data de Nascimento</Label>
                <Input
                  id="birth_date"
                  type="date"
                  {...register('birth_date')}
                />
                {errors.birth_date && (
                  <p className="text-sm text-destructive">
                    {errors.birth_date.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="role">Função *</Label>
                <Controller
                  name="role"
                  control={control}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione uma função" />
                      </SelectTrigger>
                      <SelectContent>
                        {isStudentContext ? (
                          <SelectItem value="student">Aluno</SelectItem>
                        ) : (
                          <>
                            <SelectItem value="teacher">Professor</SelectItem>
                            <SelectItem value="manager">Gerente</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.role && (
                  <p className="text-sm text-destructive">
                    {errors.role.message}
                  </p>
                )}

                <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                  {isStudentContext ? (
                    <p>
                      <strong>Aluno:</strong> Pode visualizar suas aulas e
                      progresso
                    </p>
                  ) : (
                    <>
                      <p>
                        <strong>Professor:</strong> Pode gerenciar turmas e
                        avaliar alunos
                      </p>
                      <p>
                        <strong>Gerente:</strong> Pode visualizar relatórios e
                        gerenciar operações
                      </p>
                    </>
                  )}
                </div>
              </div>

              {/* Endereço fiscal — só quando a escola emite nota fiscal, e nunca
                  para estrangeiro: o endereço aqui é o do TOMADOR na NFS-e, com
                  CEP, UF e município do IBGE. Estrangeiro não tem nenhum dos
                  três, e o formulário busca o resto pelo ViaCEP — pedir isso a
                  quem mora fora é um campo que não dá para preencher certo. Na
                  emissão, o tomador estrangeiro é identificado por passaporte,
                  não por endereço nacional. */}
              {isStudentContext && school?.fiscal_enabled && !isForeign && (
                <div className="space-y-4 rounded-md border p-4">
                  <div>
                    <p className="text-sm font-medium">
                      Endereço (nota fiscal)
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Usado na emissão da nota fiscal. Pode ser preenchido
                      depois — só é obrigatório no momento de emitir.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="zip_code">CEP</Label>
                      <div className="flex gap-2">
                        <Input
                          id="zip_code"
                          placeholder="00000-000"
                          {...register('zip_code', {
                            onBlur: handleCepSearch,
                          })}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleCepSearch}
                          disabled={isLoadingCep}
                        >
                          {isLoadingCep ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            'Buscar'
                          )}
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="street">Rua / Logradouro</Label>
                      <Input id="street" {...register('street')} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="street_number">Número</Label>
                      <Input
                        id="street_number"
                        {...register('street_number')}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="complement">Complemento</Label>
                      <Input id="complement" {...register('complement')} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="neighborhood">Bairro</Label>
                      <Input id="neighborhood" {...register('neighborhood')} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="address_city">Cidade</Label>
                      <Input id="address_city" {...register('address_city')} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="address_state">UF</Label>
                      <Input
                        id="address_state"
                        maxLength={2}
                        placeholder="CE"
                        {...register('address_state')}
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => window.history.back()}
                >
                  Cancelar
                </Button>
                <Button variant="default" type="submit" disabled={isSubmitting}>
                  {isSubmitting
                    ? 'Criando...'
                    : `Criar ${isStudentContext ? 'Aluno' : 'Membro'}`}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  )
}
